import { z } from 'zod';

import { localeSchema, type Locale } from './locales.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BLOG — pazarlama sitesinin içerik sözleşmesi
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Yazılar `blog_posts` + `blog_post_translations` tablolarında duruyor; bu
 * dosya arayüzün gördüğü BİÇİMİ tanımlıyor. Veritabanı tipleri (Prisma enum'u
 * `BlogTopic.GUIDE`) buraya SIZMIYOR — eşleme API'nin `blog.mappers.ts`
 * dosyasında.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Neden kablo üstünde küçük harf ("guide"), veritabanındaki gibi değil
 * ---------------------------------------------------------------------------
 * Konu üç yerde birden görünüyor: adres çubuğunda (`/blog?topic=automation`),
 * çeviri anahtarında (`blog.topics.automation`) ve API yanıtında. Üçünün aynı
 * dizeyi kullanması, arayüzde `topic.toLowerCase()` gibi sessiz bir dönüşüm
 * bırakmamak demek — o dönüşüm bir gün büyük harfli bir anahtar aranmasına ve
 * ham `blog.topics.GUIDE` metninin ekranda görünmesine yol açardı.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Yazıların METNİ burada DEĞİL
 * ---------------------------------------------------------------------------
 * `help-center.ts`ten farklı olarak blog içeriği kodda durmuyor: yayın günü
 * kod dağıtımına bağlı olamaz ve yönetimi `/admin` ekranından yapılacak.
 * Başlangıç içeriği bir TOHUM (`@mentorivo/shared/blog-seed`) ve tek
 * tüketicisi `packages/db/seed/blog.ts`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Konu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Süzgecin sırası da bu — dizideki sıra ekrandaki sıra.
 *
 * `all` burada YOK: o bir konu değil, "süzgeç kapalı" hâli ve yalnız arayüzde
 * bir düğme olarak var.
 */
export const BLOG_TOPICS = ['guide', 'automation', 'creative', 'analytics', 'product'] as const;

export const blogTopicSchema = z.enum(BLOG_TOPICS);
export type BlogTopic = z.infer<typeof blogTopicSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Gövde blokları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Gövde neden blok listesi — HTML de değil, Markdown da değil.
 *
 * | Biçim    | Bedeli |
 * |----------|--------|
 * | HTML     | Gelen içeriği temizlemek (sanitize) + `dangerouslySetInnerHTML`. Admin ekranı açıldığı gün bu kolon kullanıcı girdisi olur; XSS yüzeyini bugünden açmanın sebebi yok. |
 * | Markdown | Bir ayrıştırıcı, bir de onun ürettiği HTML için yine temizleyici. |
 * | Blok     | Her blok tipinin arayüzde tek bir karşılığı var; tanımadığı bir tip gelirse basılmıyor. |
 *
 * Blok listesi ayrıca admin editörünün (Faz 9) ne üretmesi gerektiğini
 * TANIMLIYOR: yeni bir görsel biçim, önce buraya bir tip olarak eklenir.
 *
 * Tablo bloğu bilerek YOK: dar ekranda tablo kendi kabında yatay kaymak
 * zorunda ve bu, gövde metninin akışını bozan ayrı bir bileşen demek.
 * Gerekirse eklenecek ilk tip o.
 */

/** Gövdedeki ana başlık — içindekiler listesi (TOC) yalnız bunlardan kuruluyor. */
export const blogHeadingBlockSchema = z.object({
  type: z.literal('heading'),
  text: z.string().min(1).max(200),
  /**
   * Bağlantı çıpası (`#kurali-ne-zaman-durdurmali`).
   *
   * Tohumda YAZILMIYOR: metinden türetiliyor ve türetme sunucuda tek yerde
   * yapılıyor (`blog.mappers.ts` → `anchorFor`). İki yerde hesaplansaydı
   * içindekiler listesindeki bağlantı ile başlığın kimliği bir gün ayrılırdı.
   */
  anchor: z.string().optional(),
});

export const blogBlockSchema = z.discriminatedUnion('type', [
  blogHeadingBlockSchema,
  z.object({ type: z.literal('subheading'), text: z.string().min(1).max(200) }),
  z.object({ type: z.literal('paragraph'), text: z.string().min(1).max(4000) }),
  z.object({
    type: z.literal('list'),
    /// `false` = madde imli. Sıralı liste "önce şu, sonra bu" demek.
    ordered: z.boolean().default(false),
    items: z.array(z.string().min(1).max(600)).min(1).max(20),
  }),
  z.object({
    type: z.literal('quote'),
    text: z.string().min(1).max(1200),
    /// Kaynak. Boşsa arayüz atıf satırını hiç basmıyor.
    cite: z.string().max(160).optional(),
  }),
  /**
   * Kenar notu — "dikkat", "kısayol", "bunu atlamayın".
   *
   * Alıntıdan farkı: alıntı BAŞKASININ sözü, not YAZININ kendi sesi. İkisi
   * ekranda da farklı duruyor.
   */
  z.object({
    type: z.literal('note'),
    title: z.string().max(120).optional(),
    text: z.string().min(1).max(1200),
  }),
  z.object({
    type: z.literal('image'),
    src: z.string().min(1).max(500),
    /// Boş dize GEÇERLİ: dekoratif görselde ekran okuyucu susmalı.
    alt: z.string().max(300),
    caption: z.string().max(300).optional(),
  }),
  /**
   * Galeri — yan yana 2 ilâ 4 görsel.
   *
   * `image` bloğundan farkı sayı değil İDDİA: tek görsel bir örnek gösterir,
   * galeri bir KARŞILAŞTIRMA yapar ("üç varyant yan yana", "önce/sonra").
   * Bu yüzden alt sınır 2 — tek öğeli bir galeri `image` bloğunun ta kendisi
   * ve iki farklı yolla aynı şeyi yazmanın anlamı yok.
   *
   * Üst sınır 4: beşinci görselde ızgara bir katalog sayfasına dönüyor ve
   * her biri, yazının okunma genişliğinde tanınamayacak kadar küçülüyor.
   * Daha fazlası gerekiyorsa o bir galeri değil, ayrı bir sayfa.
   */
  z.object({
    type: z.literal('gallery'),
    images: z
      .array(
        z.object({
          src: z.string().min(1).max(500),
          alt: z.string().max(300),
          caption: z.string().max(300).optional(),
        }),
      )
      .min(2)
      .max(4),
  }),
  z.object({ type: z.literal('divider') }),
]);

export type BlogBlock = z.infer<typeof blogBlockSchema>;
export type BlogHeadingBlock = z.infer<typeof blogHeadingBlockSchema>;

/**
 * Gövdenin tamamı.
 *
 * `Json` kolonundan OKURKEN de çalışıyor: kolonun şema baskısı yok ve bir gün
 * elle düzenlenecek. Tanınmayan blok tipini düşürmek, sayfayı 500'e
 * düşürmekten iyi — gerekçe `blog.service.ts`te.
 */
export const blogBodySchema = z.array(blogBlockSchema).max(200);

/**
 * Okuma süresi, dakika — gövdenin KENDİSİNDEN.
 *
 * Elle girilen bir sayı olsaydı yazı düzenlendikçe eskir ve kimse fark etmezdi.
 * Burada durması bilinçli: tohum betiği de (`packages/db/seed/blog.ts`), admin
 * yazma yolu da (Faz 9) aynı fonksiyonu çağırsın, iki farklı tahmin çıkmasın.
 *
 * 200 kelime/dakika yaygın kabul; başlıklar ve liste maddeleri de sayılıyor
 * çünkü okunuyorlar. Görsel ve ayraç sayılmıyor. Taban 1 dakika — "0 dk okuma"
 * bir bilgi değil, bir hata gibi duruyor.
 */
export function estimateReadMinutes(body: BlogBlock[]): number {
  let words = 0;

  for (const block of body) {
    switch (block.type) {
      case 'heading':
      case 'subheading':
      case 'paragraph':
        words += countWords(block.text);
        break;
      case 'quote':
      case 'note':
        words += countWords(block.text);
        break;
      case 'list':
        for (const item of block.items) words += countWords(item);
        break;
      case 'gallery':
        // Yalnız altyazılar okunuyor; görselin kendisi süre taşımıyor.
        for (const image of block.images) words += countWords(image.caption ?? '');
        break;
      // Görsel ve ayraç okuma süresi taşımıyor.
      case 'image':
      case 'divider':
        break;
    }
  }

  return Math.max(1, Math.round(words / 200));
}

function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sorgular
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Dizin sorgusu.
 *
 * `listQuerySchema` (common.ts) KULLANILMADI: o kiracı listelerinin
 * sayfalayıcısı ve `page`/`pageSize` istiyor. Blog dizini bugün tek sayfa —
 * arayüz on iki kartı alıp süzgeci istemcide uyguluyor, böylece konu
 * değiştirdiğinde ağa çıkılmıyor ve kartlar `layoutId` ile yerinde kalıyor.
 * `limit` yine de var: içerik yüzlerce yazıya çıktığında ilk fren burası.
 */
export const blogListQuerySchema = z.object({
  topic: blogTopicSchema.optional(),
  /// Verilmezse İngilizce. Arayüz her zaman gönderiyor (`/[locale]/blog`).
  locale: localeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(60),
});
export type BlogListQuery = z.infer<typeof blogListQuerySchema>;

export const blogDetailQuerySchema = z.object({
  locale: localeSchema.optional(),
});
export type BlogDetailQuery = z.infer<typeof blogDetailQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıtlar
// ═══════════════════════════════════════════════════════════════════════════

export interface BlogPostSummary {
  slug: string;
  topic: BlogTopic;
  title: string;
  excerpt: string;
  /**
   * ISO 8601 dize — `common.ts`in birinci serileştirme kuralı.
   *
   * `null` OLAMAZ: dizin yalnız yayınlanmış yazıları döndürüyor ve
   * yayınlanmış bir yazının tarihi var (kontrol `blog.service.ts`te).
   */
  publishedAt: string;
  readMinutes: number;
  coverImage: string | null;
  authorName: string;
  /**
   * Başlığın ve özetin GERÇEK dili.
   *
   * Çevirisi üretilen on bir dilden birinde metni olmayan yazı İngilizceye (o
   * da yoksa Türkçeye) düşüyor (`common/content-locale.ts`, 29 Eyl 2026 —
   * gizlemek, çeviriler yazılana kadar `/de/blog`u boş açmak demekti).
   * `en`/`tr` istendiğinde düşme YOK, eksik çeviri yazıyı gizliyor. Arayüz
   * kartın `lang` özniteliğini buradan basıyor.
   */
  locale: Locale;
  /// Düşülmüşse İSTENEN (eksik) dil, düşülmemişse `null` — yasal metinle
  /// aynı sözleşme (`LegalDocDetail.fallbackLocale`).
  fallbackLocale: Locale | null;
}

export interface BlogTocEntry {
  anchor: string;
  text: string;
}

/**
 * Komşu yazı — okuma bittiğinde nereye.
 *
 * Yalnız `slug` + `title`: alt bardaki bağlantı için yeterli ve bir özet daha
 * taşımak yanıtı iki kat büyütürdü.
 */
export interface BlogPostLink {
  slug: string;
  title: string;
}

export interface BlogPostDetail extends BlogPostSummary {
  body: BlogBlock[];
  /**
   * Son değişiklik. Yazının kendisi ile çevirisinin `updatedAt` değerlerinin
   * BÜYÜĞÜ — yalnız metin düzeltildiğinde yazı satırı kımıldamıyor.
   *
   * Arayüz bunu yayın gününden belirgin biçimde sonraysa basıyor; her
   * kaydetmede "güncellendi" yazmak, güncellemeyi anlamsızlaştırır.
   */
  updatedAt: string;
  authorRole: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  /// Gövdedeki `heading` bloklarından türetiliyor. İki başlıktan azsa BOŞ:
  /// tek maddelik bir içindekiler listesi gürültüden başka bir şey değil.
  toc: BlogTocEntry[];
  /// Aynı konudan en fazla üç yazı. Sunucuda hesaplanıyor — arayüz dizini
  /// ikinci kez çekmesin (`help-center.service.ts` ile aynı gerekçe).
  related: BlogPostSummary[];
  /// Yayın sırasında bir önceki / bir sonraki yazı. Konudan BAĞIMSIZ:
  /// okuma zinciri kronolojik.
  prev: BlogPostLink | null;
  next: BlogPostLink | null;
  /// Gövdenin GERÇEK dili; `fallbackLocale` doluysa istenen dilde çeviri
  /// yoktu (alanlar `BlogPostSummary`de).
  locale: Locale;
}

export interface BlogTopicCount {
  key: BlogTopic;
  count: number;
}

export interface BlogListResponse {
  posts: BlogPostSummary[];
  /**
   * Süzgeç düğmelerinin kaynağı — sayılar KONU SÜZGECİ UYGULANMADAN.
   *
   * Süzülmüş listeden hesaplansaydı bir konu seçildiğinde diğerlerinin hepsi
   * sıfır görünürdü; süzgeç bir sonuç değil, navigasyon.
   */
  topics: BlogTopicCount[];
  total: number;
  /// İSTENEN dil. Kartların her biri kendi metninin dilini ayrıca taşıyor
  /// (`BlogPostSummary.locale`): çevirisi üretilen dillerde çevirisi olmayan
  /// yazı listeden düşmüyor, İngilizce ya da Türkçe metniyle duruyor. `en` ve
  /// `tr` dizininde o dilde metni olmayan yazı hiç yok.
  locale: Locale;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hatalar
// ═══════════════════════════════════════════════════════════════════════════

export const BlogErrorCode = {
  /// Slug yok, taslak, ileri tarihli ya da bu dilde (çevirisi üretilen
  /// dillerde: bu dilde, İngilizce ya da Türkçe) metni yok — dördü de
  /// ziyaretçi için aynı şey: bu adreste okunacak bir yazı yok.
  POST_NOT_FOUND: 'BLOG_POST_NOT_FOUND',
} as const;

export type BlogErrorCode = (typeof BlogErrorCode)[keyof typeof BlogErrorCode];
